/**
 * @fileoverview Browser push notification service worker registration and subscription management.
 * Handles push notification permissions, service worker registration, and VAPID-based subscriptions.
 */

import store from "@/store";
import { rest } from "@/store/api";
import _ from "lodash";

/**
 * Registers a service worker if it's supported by the browser.
 *
 * @returns {Promise<ServiceWorkerRegistration | null>} The service worker registration or null if unsupported
 *
 * @example
 * const registration = await registerServiceWorker();
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if ("serviceWorker" in navigator) {
    try {
      return await navigator.serviceWorker.register("/sw.js");
    } catch (error) {
      console.error("Error registering service worker:", error);
    }
  }
  return null;
}

/**
 * Requests permission from the user to display notifications.
 *
 * @returns {Promise<NotificationPermission>} The notification permission status
 * @throws {Error} When notification permission is not granted
 *
 * @example
 * const permission = await requestNotificationPermission();
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("Notification permission not granted.");
  }
  return permission;
}

/**
 * Subscribes a user to push notifications, asking for permission if needed, and sends the subscription to the server.
 *
 * @param {string} email - User email address for subscription
 * @returns {Promise<void>}
 * @throws {Error} When VAPID public key is not set
 *
 * @example
 * await subscribeToPush('user@example.com');
 */
export async function subscribeToPush(email: string): Promise<void> {
  const registration = await navigator.serviceWorker.ready;
  let subscription = await registration.pushManager.getSubscription();

  if (!subscription) {
    const permission = Notification.permission;
    if (permission !== "granted") {
      await requestNotificationPermission();
    }

    const vapidPublicKey = getVapidPublicKey();
    if (!vapidPublicKey) {
      throw new Error("VAPID public key is not set.");
    }

    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });
  }

  await rest("post", "notification_subscribe", { email, subscription });
}

/**
 * Unsubscribes a user from push notifications and sends the unsubscription to the server.
 *
 * @param {string} email - User email address to unsubscribe
 * @returns {Promise<void>}
 *
 * @example
 * await unsubscribeFromPush('user@example.com');
 */
export async function unsubscribeFromPush(email: string): Promise<void> {
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();

  if (subscription) {
    await subscription.unsubscribe();
    await rest("post", "notification_unsubscribe", { email, subscription });
  }
}

/**
 * Retrieves the VAPID public key from the store.
 *
 * @returns {string | null} VAPID public key or null if not configured
 */
function getVapidPublicKey(): string | null {
  return _.get(store, "state.modules.notification.vapidPublicKey", null);
}

/**
 * Converts a base64-encoded string to a Uint8Array for VAPID key format.
 *
 * @param {string} base64String - Base64 URL-safe encoded string
 * @returns {Uint8Array} Decoded byte array
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/\-/g, "+").replace(/_/g, "/");

  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
